import type { AppointmentFavorite, FavoritesSlot, GownPickerItem, SaveFavoritesRequest } from '@fitting-room/contracts'

/**
 * The favorites picker's working copy: what the stylist has added and taken
 * off since opening it, over the list as the server last answered it.
 * Nothing is sent until Save, and Cancel throws this away.
 *
 * Pure, so the rules the dialog draws — what may be taken off, when the list
 * is full, what Save sends — are tested without a browser.
 */
export interface FavoritesDraft {
  /** Gowns added in this sitting, in the order they were clicked. */
  readonly added: readonly GownPickerItem[]
  /** Gowns on the saved list that this sitting takes off — only ever a stylist's. */
  readonly removed: ReadonlySet<string>
}

export const EMPTY_DRAFT: FavoritesDraft = { added: [], removed: new Set() }

/** What the drawer says under a gown a stylist added. The customer's own say nothing. */
export function addedByLabel(favorite: Pick<AppointmentFavorite, 'addedBy' | 'addedByName'>): string | undefined {
  if (favorite.addedBy !== 'staff') return undefined
  return `Added by ${favorite.addedByName ?? 'a stylist'}`
}

/** A stylist may take off what a stylist added. The customer's own picks are theirs to change. */
export function canRemove(favorite: Pick<AppointmentFavorite, 'addedBy'>): boolean {
  return favorite.addedBy === 'staff'
}

/** Why the customer's own pick has no remove control — on hover, and to a screen reader. */
export const CUSTOMER_PICK_REASON = 'The customer picked this one. Only they can take it off, in their fitting room.'

/** Whether a gown is on the list as it would be saved: kept from before, or added now. */
export function isOnList(draft: FavoritesDraft, saved: readonly AppointmentFavorite[], garmentId: string): boolean {
  if (draft.added.some((item) => item.garmentId === garmentId)) return true
  return saved.some((f) => f.garmentId === garmentId) && !draft.removed.has(garmentId)
}

/**
 * A click on a gown in the catalog: on the list it comes, clicked again it
 * goes — tperent's picker toggles the same way. A gown already saved on the
 * list is not added twice; one taken off in this sitting is put back.
 */
export function toggleGown(draft: FavoritesDraft, saved: readonly AppointmentFavorite[], item: GownPickerItem): FavoritesDraft {
  if (draft.added.some((added) => added.garmentId === item.garmentId)) {
    return { ...draft, added: draft.added.filter((added) => added.garmentId !== item.garmentId) }
  }
  if (draft.removed.has(item.garmentId)) {
    const removed = new Set(draft.removed)
    removed.delete(item.garmentId)
    return { ...draft, removed }
  }
  if (saved.some((f) => f.garmentId === item.garmentId)) return draft
  return { ...draft, added: [...draft.added, item] }
}

/** The × on the list: an addition from this sitting is simply dropped; a saved stylist's gown is marked to come off. */
export function takeOff(draft: FavoritesDraft, saved: readonly AppointmentFavorite[], garmentId: string): FavoritesDraft {
  if (draft.added.some((item) => item.garmentId === garmentId)) {
    return { ...draft, added: draft.added.filter((item) => item.garmentId !== garmentId) }
  }
  const favorite = saved.find((f) => f.garmentId === garmentId)
  if (!favorite || !canRemove(favorite)) return draft
  return { ...draft, removed: new Set([...draft.removed, garmentId]) }
}

/** Put back a saved gown this sitting had marked to come off. */
export function keep(draft: FavoritesDraft, garmentId: string): FavoritesDraft {
  const removed = new Set(draft.removed)
  removed.delete(garmentId)
  return { ...draft, removed }
}

export function isDirty(draft: FavoritesDraft): boolean {
  return draft.added.length > 0 || draft.removed.size > 0
}

/** What Save sends. */
export function saveRequest(draft: FavoritesDraft): SaveFavoritesRequest {
  return { add: draft.added.map((item) => item.garmentId), remove: [...draft.removed] }
}

export interface SlotRoom {
  /** On the stylist's list once this sitting is saved. */
  readonly count: number
  /** Nothing more may be added. Only a list with a size to it is ever full. */
  readonly full: boolean
  /** The line over the list: "Favorite · 6", or "Interested · 4 of 5". */
  readonly summary: string
  /** Said only when it is full, in the list's own words. */
  readonly fullMessage?: string
}

/** How full the stylist's list would be, counting this sitting's additions and removals. */
export function slotRoom(slot: FavoritesSlot, saved: readonly AppointmentFavorite[], draft: FavoritesDraft): SlotRoom {
  const takenOff = saved.filter((f) => f.slotKey === slot.key && draft.removed.has(f.garmentId)).length
  const count = slot.used - takenOff + draft.added.length
  if (!slot.limited) return { count, full: false, summary: `${slot.label} · ${count}` }
  const full = count >= slot.capacity
  return {
    count,
    full,
    summary: `${slot.label} · ${count} of ${slot.capacity}`,
    ...(full ? { fullMessage: `${slot.label} is full — it holds ${slot.capacity} ${slot.capacity === 1 ? 'gown' : 'gowns'}. Take one off to add another.` } : {}),
  }
}

/**
 * The line under a gown's style #: the name when it says something the
 * style # doesn't, its color, whether it is off the rack, and — in the
 * drawer — the list it is on.
 */
export function gownDetail(gown: { code?: string | undefined; name: string; color?: string | undefined; offTheRack: boolean }, listLabel?: string): string {
  return [gown.code && gown.name !== gown.code ? gown.name : undefined, gown.color, gown.offTheRack ? 'Off the rack' : undefined, listLabel]
    .filter(Boolean)
    .join(' · ')
}
